import SearchGrantableUsers from '@Application/SubjectTypeDefinitions/Queries/SearchGrantableUsers';
import searchGrantableUsers from '@Application/SubjectTypeDefinitions/searchGrantableUsers';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import type SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import GrantableUserView from '@UI/Store/SubjectTypeDefinitions/Views/GrantableUserView';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Autocomplete, Button, Dropdown } from '@verclam/ui-kit';

const SEARCH_DELAY_IN_MILLISECONDS = 300;
const SEARCH_MINIMUM_LENGTH = 2;

type Props = {
	readonly grants: SubjectTypeGrantsFormData;
	readonly accountId: number;
	/** Receives the user and the level to grant; the parent adds the line and remembers the name. */
	readonly onAdd: (user: GrantableUserView, level: string) => void;
	readonly isReadOnly: boolean;
};

/** Grants a user: search the active users (2 characters at least, latest answer only), pick a level, then add. */
const AddUserGrant: React.FC<Props> = ({ grants, accountId, onAdd, isReadOnly }) => {
	const dispatch = useDispatch();
	const accessLevels = useSelector((state) => state.subjectTypeDefinitionsStore.accessLevels);
	const [suggestions, setSuggestions] = React.useState<readonly GrantableUserView[]>([]);
	const [isSearching, setIsSearching] = React.useState(false);
	const [selectedUser, setSelectedUser] = React.useState<GrantableUserView | null>(null);
	const [pickedLevel, setPickedLevel] = React.useState<string | null>(null);
	const latestSearch = React.useRef(0);
	const level = pickedLevel ?? accessLevels?.[0]?.value ?? null;

	const handleSearch = React.useCallback(
		(query: string) => {
			latestSearch.current += 1;
			const search = latestSearch.current;
			setIsSearching(true);

			dispatch(searchGrantableUsers(new SearchGrantableUsers(accountId, query)))
				.then((action) => {
					if (search === latestSearch.current && action.type === 'GRANTABLE_USERS_FOUND') {
						setSuggestions(
							action.payload
								.map((user) => new GrantableUserView(user))
								.filter((user) => !grants.hasGrant('user', user.id)),
						);
					}
				})
				.catch(ignoreHandledError)
				.finally(() => {
					if (search === latestSearch.current) {
						setIsSearching(false);
					}
				});
		},
		[dispatch, accountId, grants],
	);

	const handleUserChange = React.useCallback(
		(selection: GrantableUserView | readonly GrantableUserView[] | null) => {
			setSelectedUser(
				selection === null || Array.isArray(selection) ? null : (selection as GrantableUserView),
			);
		},
		[],
	);

	const handleAddClick = React.useCallback(() => {
		if (selectedUser === null || level === null) {
			return;
		}

		onAdd(selectedUser, level);
		setSelectedUser(null);
	}, [onAdd, selectedUser, level]);

	return (
		<Grid columns={12} columnGap={1} alignItems="center">
			<GridItem columnSize={{ xs: 12, xl: 6 }}>
				<Autocomplete<GrantableUserView>
					name="grantUser"
					label="Utilisateur"
					isLabelHidden
					placeholder="Rechercher un utilisateur…"
					field="displayName"
					value={selectedUser}
					onChange={handleUserChange}
					suggestions={suggestions}
					onSearch={handleSearch}
					delay={SEARCH_DELAY_IN_MILLISECONDS}
					minLength={SEARCH_MINIMUM_LENGTH}
					loading={isSearching}
					showEmptyMessage
					emptyMessage="Aucun utilisateur trouvé"
					disabled={isReadOnly}
				/>
			</GridItem>
			<GridItem columnSize={{ xs: 8, xl: 4 }}>
				<Dropdown
					name="grantUserLevel"
					label="Niveau d'accès de l'utilisateur"
					isLabelHidden
					options={accessLevels ?? []}
					optionLabel="label"
					optionValue="value"
					value={level}
					disabled={isReadOnly}
					onChange={setPickedLevel}
				/>
			</GridItem>
			<GridItem columnSize={{ xs: 4, xl: 2 }}>
				<Button
					type="button"
					size="sm"
					severity="secondary"
					label="Ajouter"
					disabled={isReadOnly || selectedUser === null}
					onClick={handleAddClick}
				/>
			</GridItem>
		</Grid>
	);
};

export default AddUserGrant;
